/**
 * Runtime physics parameters for a vehicle, computed from a VehicleBlueprint
 * plus optional upgrades and zone damage.
 *
 * Distinct from VehicleBlueprint.stats (VehicleStats), which is the
 * author-time input for maxHealth / armour / mass.
 */
export interface CarStats {
  /** Forward speed cap (m/s). Replaces bp.handling.maxSpeed. */
  topSpeed: number;
  /** Throttle power (m/s²). Replaces bp.handling.acceleration. */
  accel: number;
  /** Brake / deceleration power (m/s²). Replaces bp.handling.braking. */
  brakeForce: number;
  /** Lateral grip recovery factor 0–1. Replaces bp.handling.traction. */
  traction: number;
  /** Handbrake drift intensity 0–1. Replaces bp.handling.drift. */
  drift: number;
  /** Steering authority (rad/s). Replaces bp.handling.turnRate. */
  turnRate: number;
  /**
   * Vehicle mass (kg-scaled). Passive — not wired into kinematic model yet.
   * Future hook: accel_effective = accel * (REFERENCE_MASS / mass)
   */
  mass: number;
  /**
   * Boost magnitude scalar. Applied around the existing boost constants:
   *   effSpeedMult = 1 + (BOOST_SPEED_MULTIPLIER - 1) * boostPower
   *   effAccelMult = 1 + (BOOST_ACCEL_MULTIPLIER - 1) * boostPower
   * Default 1.0 → identical to previous hardcoded behaviour.
   */
  boostPower: number;
  /**
   * High-speed grip retention factor. Replaces hardcoded 0.6:
   *   grip = 1.0 - speedRatio * (1 - downforce)
   * Default 0.4 → 1 - 0.4 = 0.6, identical to previous behaviour.
   */
  downforce: number;
  /**
   * Gear shift speed multiplier for EngineAudio. Audio-tuning only; no
   * physics effect. Default 1.0 = unmodified thresholds.
   */
  gearShiftSpeed: number;
}

/** Per-upgrade-slot level. 0 = stock, 1–3 = upgrade tiers. */
export interface CarUpgrade {
  engine:     0 | 1 | 2 | 3;
  gearbox:    0 | 1 | 2 | 3;
  turbo:      0 | 1 | 2 | 3;
  suspension: 0 | 1 | 2 | 3;
  aero:       0 | 1 | 2 | 3;
  brakes:     0 | 1 | 2 | 3;
  boost:      0 | 1 | 2 | 3;
}

/**
 * Multiplicative stat damage penalties from zone hits.
 * Each value is a multiplier in (0, 1] where 1.0 = undamaged.
 * Values are clamped to a minimum of 0.1 inside computeStats.
 *
 * This is the zone-damage hookpoint. The server zone simulation is
 * not modified in this iteration — no network sync yet.
 */
export type StatDamage = Partial<Record<keyof CarStats, number>>;

/** All slots at level 0 — stock vehicle with no upgrades applied. */
export const DEFAULT_UPGRADES: CarUpgrade = {
  engine:     0,
  gearbox:    0,
  turbo:      0,
  suspension: 0,
  aero:       0,
  brakes:     0,
  boost:      0,
};
